import { Link } from "react-router-dom";
import { ArrowRight, Sun, Zap, Cable, FileCheck, Shield, Leaf } from "lucide-react";

const SOLAR_PILLARS = [
  { icon: Sun, code: "01", title: "Generation Capacity", body: "Proposed 30-33 MW solar installation co-located with the Lobatse Leather Park and BMC anchor facilities.", value: "30-33 MW" },
  { icon: Zap, code: "02", title: "Power Offtake", body: "Direct supply to the Leather Park tannery, finishing and manufacturing lines. Surplus exported to BPC grid under IPP framework.", value: "Dual offtake" },
  { icon: Cable, code: "03", title: "Grid Interconnection", body: "BPC grid connection enquiry and interconnection study. Designed to BPC technical standards from inception.", value: "BPC framework" },
  { icon: FileCheck, code: "04", title: "Regulatory Licensing", body: "BERA (Botswana Energy Regulatory Authority) IPP generation licence under Botswana's established IPP and PPA frameworks.", value: "BERA-licensed" },
];

const APPROVAL_PATHWAY = [
  { n: "01", status: "In Progress", title: "BPC Grid Connection Enquiry", body: "Application to Botswana Power Corporation for grid connection assessment and interconnection study." },
  { n: "02", status: "In Progress", title: "Pre-Feasibility Study", body: "Technical and financial pre-feasibility covering solar generation, storage options, and grid integration." },
  { n: "03", status: "Proposed", title: "EIA Screening", body: "Environmental Impact Assessment for solar development and supporting infrastructure." },
  { n: "04", status: "Proposed", title: "BERA Licensing Review", body: "Regulatory review with Botswana Energy Regulatory Authority for IPP generation licence." },
  { n: "05", status: "Next Step", title: "Offtaker Engagement", body: "Engagement with BPC and Leather Park tenants for Power Purchase Agreement (PPA) structuring." },
  { n: "06", status: "Next Step", title: "EPC Partner Selection", body: "Selection of EPC contractor, technology partners and co-investors for Phase 1 execution." },
];

const SOLAR_BENEFITS = [
  ["Industrial Power", "Reliable, low-cost power for tannery, finishing and manufacturing operations"],
  ["ESG Credentials", "Renewable energy backing strengthens ESG positioning for luxury buyer compliance"],
  ["Grid Revenue", "Surplus generation sold to BPC under IPP framework — second revenue line"],
  ["Cost Stability", "Long-tenor PPA insulates the Leather Park from grid tariff inflation"],
  ["Carbon Reporting", "ISO 14064 alignment and Higg FEM-compatible emissions baseline from day one"],
  ["Cluster Effect", "Anchor power infrastructure attracts downstream industrial tenants to the SEZ"],
];

export default function Solar() {
  return (
    <div data-testid="page-solar" className="bg-[#F9F9F6]">
      {/* HERO */}
      <section data-testid="solar-hero" className="relative min-h-[80vh] flex items-end overflow-hidden bg-[#1A1A1A]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1509391366360-2e959784a276?crop=entropy&cs=srgb&fm=jpg&q=80&w=2000)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.55,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/30 via-[#1A1A1A]/60 to-[#1A1A1A]/95" />
        <div className="absolute inset-0 grain" />

        <div className="absolute top-24 md:top-28 left-0 right-0 z-10">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
            <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.3em] text-[#E5C4A1]">
              <span className="h-px w-10 bg-[#E5C4A1]" />
              <span>LRG Holdings</span>
              <span className="text-[#A8A8A0]">/</span>
              <span className="text-[#C7C7BF]">Solar IPP</span>
              <span className="text-[#A8A8A0]">/</span>
              <span className="text-[#C7C7BF]">Lobatse, Botswana</span>
            </div>
          </div>
        </div>

        <div className="relative w-full max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 pb-16 md:pb-24 pt-32">
          <h1
            data-testid="solar-headline"
            className="font-serif text-[#F9F9F6] text-[44px] sm:text-6xl md:text-7xl lg:text-[96px] leading-[0.95] tracking-[-0.02em] rise"
          >
            Lobatse Leather Park
            <br />
            <span className="italic text-[#E5C4A1]">Solar Project.</span>
          </h1>
          <p className="font-serif text-[#E5C4A1] text-xl md:text-2xl lg:text-[28px] leading-snug tracking-tight mt-6 max-w-4xl">
            A proposed 30-33 MW solar IPP supporting Botswana&rsquo;s meat and leather value chain in Lobatse.
          </p>
          <p className="mt-6 text-sm md:text-base text-[#C7C7BF] max-w-2xl">
            Co-located with the Leather Park and BMC facilities. Designed under the BPC grid connection framework and BERA IPP licensing regime.
          </p>
        </div>
      </section>

      {/* PROJECT SUMMARY STRIP */}
      <section data-testid="solar-summary-strip" className="border-y border-[#1A1A1A] bg-[#F9F9F6]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            ["Capacity", "30-33 MW"],
            ["Location", "Lobatse, Botswana"],
            ["Framework", "BPC + BERA"],
            ["Phase", "Pre-feasibility"],
          ].map(([k, v]) => (
            <div key={k}>
              <p className="eyebrow">{k}</p>
              <p className="font-serif text-2xl md:text-3xl tracking-tight mt-3">{v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHY SOLAR — pillars */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32" data-testid="solar-pillars">
        <div className="grid grid-cols-12 gap-8 mb-12">
          <div className="col-span-12 md:col-span-6">
            <p className="eyebrow">01 / Project Pillars</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] mt-5">
              Power, regulation, offtake.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
            <p className="text-base text-[#1A1A1A] leading-relaxed">
              Four load-bearing components, each anchored to Botswana&rsquo;s established IPP and PPA framework.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 border-l border-t border-[#1A1A1A]">
          {SOLAR_PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.code} data-testid={`solar-pillar-${p.code}`} className="border-r border-b border-[#1A1A1A] p-8 md:p-10 bg-[#F9F9F6] hover:bg-white transition-colors">
                <div className="flex items-baseline justify-between">
                  <span className="font-serif text-2xl md:text-3xl text-[#5C2018]">{p.code}</span>
                  <span className="font-serif text-xl md:text-2xl text-[#5C2018] tracking-tight">{p.value}</span>
                </div>
                <div className="mt-8 w-10 h-10 border border-[#1A1A1A] flex items-center justify-center">
                  <Icon size={18} />
                </div>
                <h3 className="font-serif text-2xl md:text-3xl tracking-tight mt-6 leading-tight">{p.title}</h3>
                <p className="mt-4 text-base text-[#1A1A1A] leading-relaxed max-w-md">{p.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* APPROVAL PATHWAY */}
      <section data-testid="solar-pathway" className="bg-[#1A1A1A] text-[#F9F9F6]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8 mb-12">
            <div className="col-span-12 md:col-span-6">
              <p className="eyebrow text-[#A8A8A0]">02 / Approval Pathway</p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] mt-5">
                Regulatory roadmap.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
              <p className="text-sm text-[#C7C7BF] leading-relaxed">
                Each step must be completed before construction can begin. Status as of current structuring phase.
              </p>
            </div>
          </div>
          <div className="border-t border-[#333330]">
            {APPROVAL_PATHWAY.map((s) => (
              <div key={s.n} data-testid={`pathway-step-${s.n}`} className="grid grid-cols-12 gap-4 py-6 border-b border-[#333330] items-center">
                <span className="col-span-2 md:col-span-1 font-serif text-2xl text-[#E5C4A1]">{s.n}</span>
                <span className="col-span-10 md:col-span-2 text-[10px] uppercase tracking-[0.22em] border border-[#E5C4A1] text-[#E5C4A1] px-2 py-1 inline-block w-fit">
                  {s.status}
                </span>
                <div className="col-span-12 md:col-span-9">
                  <p className="font-serif text-xl md:text-2xl tracking-tight leading-snug">{s.title}</p>
                  <p className="text-sm text-[#C7C7BF] mt-2 leading-relaxed max-w-3xl">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLAR BENEFITS */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32" data-testid="solar-benefits">
        <div className="grid grid-cols-12 gap-8 mb-12">
          <div className="col-span-12 md:col-span-6">
            <p className="eyebrow">03 / Strategic Value</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] mt-5">
              Why solar, why now.
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-l border-t border-[#1A1A1A]">
          {SOLAR_BENEFITS.map(([k, v]) => (
            <div key={k} className="border-r border-b border-[#1A1A1A] p-8 bg-[#F9F9F6] hover:bg-white transition-colors">
              <div className="w-9 h-9 border border-[#1A1A1A] flex items-center justify-center">
                <Leaf size={16} />
              </div>
              <h3 className="font-serif text-xl md:text-2xl tracking-tight mt-6 leading-tight">{k}</h3>
              <p className="text-sm text-[#1A1A1A] mt-3 leading-relaxed">{v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-20" data-testid="solar-transparency">
        <div className="border border-[#5C2018] p-8 md:p-10 bg-white">
          <div className="flex items-start gap-4">
            <Shield className="text-[#5C2018] flex-shrink-0 mt-1" size={22} />
            <div>
              <p className="eyebrow text-[#5C2018]">Transparency Note</p>
              <p className="font-serif text-xl md:text-2xl tracking-tight mt-3 leading-snug max-w-3xl">
                The solar project is in <span className="italic">pre-feasibility and structuring phase</span>. No construction, generation licence or PPA has been signed. All figures are indicative pending feasibility study and BPC interconnection assessment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#5C2018] text-[#F9F9F6]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-24 grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-8">
            <p className="eyebrow text-[#E5C4A1]">Engage</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight mt-4">
              IPP capital, EPC partners and offtakers welcome to engage.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <Link
              to="/contact"
              data-testid="solar-cta-contact"
              className="inline-flex items-center gap-3 px-7 py-4 bg-[#F9F9F6] text-[#1A1A1A] text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-[#1A1A1A] hover:text-[#F9F9F6] transition-colors"
            >
              Contact LRG <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
